import React from 'react';

import { Button, Divider, Modal, TextField, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { Box } from '@mui/system';
import { SaveSheet, SheetStatus } from 'src/common/types/SaveSheet';

import { ReactComponent as CancelIcon } from '../../../../common/assets/icons/cancel_icon_gray.svg';
import { drawerWidth } from '../../../../common/components/tfa-dashboard-layout/styles';
import { SelectableCode } from '../../../../common/types/Codes';

import ClearButton from './ClearButton';
import EditTextButton from './EditTextButton';
import ExportButton from './ExportButton';
import MiniCPTCode from './MiniCPTCode';
import NoteText from './NoteText';

type Props = {
  note: SaveSheet | null | undefined;
  refreshResults: (text: string) => void;
  isLoading: boolean;
  saveIsLoading: boolean;
  selectedCodes: SelectableCode[];
  clearSelectedCodes: () => void;
  selectCode: (code: SelectableCode) => void;

  // For the search results when uploading multiple notes
  notesCount: number;
  page: number;
  onChangePage: (page: number) => void;
};

const OpNoteHeader: React.FC<Props> = ({
  note,
  refreshResults,
  isLoading,
  saveIsLoading,
  selectedCodes,
  clearSelectedCodes,
  selectCode,
  notesCount,
  page,
  onChangePage,
}) => {
  const [popup, setPopup] = React.useState<null | 'view' | 'edit'>(null);
  const [editedSearchText, setEditedSearchText] = React.useState(
    note?.scanText ?? ''
  );

  React.useEffect(() => {
    setEditedSearchText(note?.scanText ?? '');
  }, [note?.scanText]);

  const closePopup = () => {
    setPopup(null);
    setEditedSearchText(note?.scanText ?? '');
  };

  const confirmChanges = () => {
    if (editedSearchText.trim().length > 0) {
      refreshResults(editedSearchText.trim());
      setPopup(null);
    }
  };

  return (
    <Box
      id='op-note-header'
      sx={{
        width: window.location.host.includes('admin')
          ? `calc(100% - ${drawerWidth}px)`
          : '100%',
        marginTop: window.location.host.includes('admin') ? '0px' : '20px',
        position: 'fixed',
        zIndex: 1,
        backgroundColor: '#81B5CA',
      }}
    >
      {notesCount > 1 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '-24px',
            marginTop: '8px',
            paddingRight: '16px',
            minWidth: '100px',
          }}
        >
          <Pagination
            sx={{
              '& ul > li:not(:first-of-type):not(:last-of-type)': {
                display: 'none',
              }, // this hides the numbers,
              'ul > li:first-of-type, ul > li:last-of-type': {
                margin: 0,
              },
              '.MuiPagination-ul': {
                justifyContent: 'center',
                flexWrap: 'nowrap',
              },
              'ul > li .MuiButtonBase-root': {
                borderRadius: '12px',
              },
              'ul > li:first-of-type > button, ul > li:last-of-type > button': {
                backgroundColor: '#D9E9ED',
              },
              '& .MuiPaginationItem-icon': {
                color: '#266071',
              },
            }}
            count={notesCount}
            variant='text'
            size='medium'
            color='secondary'
            onChange={(e, p) => {
              onChangePage(p - 1);
            }}
            page={page + 1}
            disabled={isLoading || saveIsLoading}
          />
        </Box>
      )}

      <Box
        sx={{
          gap: '24px',
          display: 'flex',
          flexWrap: {
            xs: 'wrap',
            md: 'nowrap',
          },
          alignItems: 'center',
          borderBottom: '1px solid rgba(82, 86, 98, 0.20)',
          padding: {
            xs: '24px 16px',
            lg: '24px 60px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: 'rgba(0, 33, 69, 0.50)',
              marginRight: '20px',
              paddingTop: '4px',
              lineHeight: '20px',
            }}
          >
            NOTE
          </Typography>
          <NoteText onViewMore={() => setPopup('view')} lineCount={2}>
            {note?.scanText}
          </NoteText>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          <EditTextButton
            isDisabled={isLoading}
            onClick={() => setPopup('edit')}
          />
          {note?.status === SheetStatus.FINALIZED && (
            <ExportButton isDisabled={isLoading} note={note} />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          gap: '24px',
          display: 'flex',
          flexWrap: {
            xs: 'wrap',
            md: 'nowrap',
          },
          borderBottom: '1px solid rgba(82, 86, 98, 0.20)',
          padding: {
            xs: '12px 16px',
            lg: '12px 60px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
            flex: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: 'rgba(0, 33, 69, 0.50)',
                marginRight: '2px',
              }}
            >
              CPT CODES
            </Typography>
          </Box>
          {selectedCodes.map((code) => (
            <MiniCPTCode key={code.code} code={code} onRemove={selectCode} />
          ))}
        </Box>
        <Box>
          <ClearButton
            isDisabled={isLoading || selectedCodes.length === 0}
            onClick={clearSelectedCodes}
          />
        </Box>
      </Box>
      <Modal open={!!popup} onClose={() => closePopup()}>
        <Box
          sx={{
            position: 'absolute' as const,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: '100%',
              backgroundColor: 'appMerino.main',
              padding: {
                xs: '20px 20px 20px 60px',
                md: '20px 40px 40px 60px',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '90px',
                width: '100%',
              }}
            >
              <Button
                onClick={() => closePopup()}
                variant='text'
                startIcon={<CancelIcon />}
              >
                <Typography color='text.secondary'>
                  {popup === 'view' ? 'Back' : 'Cancel'}
                </Typography>
              </Button>
              {popup === 'edit' && (
                <Button
                  onClick={confirmChanges}
                  variant='contained'
                  color='darkBlue'
                  disableElevation
                  sx={{
                    width: 'auto',
                    height: 48,
                    borderRadius: '15px',
                  }}
                >
                  <Typography color='white'>Confirm Changes</Typography>
                </Button>
              )}
            </Box>
            <Divider sx={{ marginTop: '16px' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                backgroundColor: 'appMerino.main',
                marginTop: '20px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'rgba(0, 33, 69, 0.50)',
                  marginBottom: '8px',
                }}
              >
                {popup === 'view' ? 'VIEW NOTE' : 'EDIT NOTE'}
              </Typography>
              <TextField
                onChange={(e) => setEditedSearchText(e.target.value ?? '')}
                variant='standard'
                multiline
                fullWidth
                value={editedSearchText}
                sx={{
                  mb: '30px',
                }}
                InputProps={{ disableUnderline: true, spellCheck: false }}
                inputProps={{ style: { color: '#002145B2' } }}
              />
              <Divider />
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default OpNoteHeader;
